import { IAPIRoutes } from "../../../../config";

export type IPatientRepository = ReturnType<typeof PatientRepositoryFactory>;

export const PatientRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getPatientListAPI: IGetPatientListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientList.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientEventListAPI: IGetPatientEventListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientEventList
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientSummaryAPI: IGetPatientSummaryAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientSummary
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientCircadiaVitalAPI: IGetPatientCircadiaVitalAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientCircadiaVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientPCCVitalAPI: IGetPatientPCCVitalAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCVital
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientVitalGraphAPI: IGetPatientVitalGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientVitalGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.resident_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientRRGraphAPI: IGetPatientRRGraphAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientRRGraph
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotesAPI: IGetPatientNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientNotificationSettingsAPI: IGetPatientNotificationSettingsAPI =
    async (payload) =>
      GET(
        APIRoutes.apiGetPatientNotificationSettings
          .replace("{clinic_id}", payload.clinic_id?.toString())
          .replace("{resident_id}", payload.patient_id?.toString()),
        payload,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const getDiagnosesAPI: IGetDiagnosesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetDiagnoses
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{resident_id}", payload.resident_id.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPCCNotesAPI: IGetPatientPCCNotesAPI = async (payload) =>
    GET(
      APIRoutes.apiGetPatientPCCNotes
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientHmacAPI: IGetPatientHmacAPI = async (payload) =>
    GET(APIRoutes.apiGetUserHmac, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const addPatientNoteAPI: IAddPatientNoteAPI = async (payload) =>
    POST(
      APIRoutes.apiAddPatientNote
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{resident_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const generateCircadiaIQAPI: IGetGenerateCircadiaIQAPI = async (payload) =>
    POST(
      APIRoutes.apiGenerateCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      {
        type: payload.type,
        date: payload.date,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getCircadiaIQAPI: IGetCircadiaIQAPI = async (payload) =>
    GET(
      APIRoutes.apiGetCircadiaIQ
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getPatientListDashboardAPI: IGetPatientListDashboardAPI = async (
    payload
  ) =>
    GET(
      APIRoutes.apiGetPatientListDashboard.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      {
        floor_ids: payload.floorIds,
        no_of_days: payload.no_of_days,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  return {
    getPatientListAPI,
    getPatientEventListAPI,
    getPatientSummaryAPI,
    getPatientCircadiaVitalAPI,
    getPatientPCCVitalAPI,
    getPatientVitalGraphAPI,
    getPatientRRGraphAPI,
    getPatientNotesAPI,
    getPatientNotificationSettingsAPI,
    getDiagnosesAPI,
    getPCCNotesAPI,
    addPatientNoteAPI,
    getPatientHmacAPI,
    generateCircadiaIQAPI,
    getCircadiaIQAPI,
    getPatientListDashboardAPI,
  };
};
